import api, { getUserId } from './api';

const DB_NAME = 'shepherds-direct-recordings-v1';
const PART_BYTES = 20 * 1024 * 1024;
const NEW_PART_BYTES = 5 * 1024 * 1024;
const TRANSPORT = 'e2-direct-v8';
const MIGRATABLE_TRANSPORT = 'spacebyte-direct-v4';
const LEGACY_TRANSPORT = 'legacy';
const MAX_QUEUED_BYTES = 128 * 1024 * 1024;
const active = new Map();
if (typeof window !== 'undefined') window.addEventListener('beforeunload', event => {
  if (!active.size) return;
  event.preventDefault(); event.returnValue = '';
});
const sleep = ms => new Promise(resolve => window.setTimeout(resolve, ms));
const dbPromise = () => new Promise((resolve, reject) => {
  if (!window.indexedDB) { reject(new Error('This browser cannot keep a recoverable recording. Use a browser with IndexedDB enabled.')); return; }
  const request = indexedDB.open(DB_NAME, 1);
  request.onupgradeneeded = () => {
    request.result.createObjectStore('sessions', { keyPath: 'id' });
    const chunks = request.result.createObjectStore('chunks', { keyPath: ['recordingId', 'sequence'] });
    chunks.createIndex('recordingId', 'recordingId');
  };
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(new Error('Browser recording storage is unavailable. Allow storage and try again.'));
});
async function acquireCaptureLock(id) {
  if (!navigator.locks?.request) throw new Error('Safe recording recovery needs a browser with Web Locks support. Use a current Chrome, Edge, Firefox or Safari browser.');
  return new Promise((resolve, reject) => {
    let release;
    const held = new Promise(done => { release = done; });
    navigator.locks.request(`shepherds-direct-recording:${id}`, { ifAvailable: true }, async lock => {
      if (!lock) { reject(new Error('This recording is active in another browser tab. Close that capture/upload before resuming here.')); return; }
      resolve(release);
      await held;
    }).catch(reject);
  });
}
async function read(store, key, all = false) {
  const db = await dbPromise();
  try { return await new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readonly');
    const request = all ? tx.objectStore(store).getAll() : tx.objectStore(store).get(key);
    request.onsuccess = () => resolve(request.result); request.onerror = () => reject(request.error);
  }); } finally { db.close(); }
}
async function chunksFor(id) {
  const db = await dbPromise();
  try { return await new Promise((resolve, reject) => {
    const request = db.transaction('chunks', 'readonly').objectStore('chunks').index('recordingId').getAll(id);
    request.onsuccess = () => resolve(request.result.sort((a, b) => a.sequence - b.sequence)); request.onerror = () => reject(request.error);
  }); } finally { db.close(); }
}
async function write(action) {
  const db = await dbPromise();
  try { return await new Promise((resolve, reject) => {
    const tx = db.transaction(['sessions', 'chunks'], 'readwrite');
    tx.oncomplete = resolve;
    tx.onerror = () => reject(new Error('Recording could not be saved in this browser. Storage may be full. Keep this tab open.'));
    tx.onabort = () => reject(new Error('Browser recording storage was interrupted.'));
    action(tx.objectStore('sessions'), tx.objectStore('chunks'));
  }); } finally { db.close(); }
}
function emit(session, state, message = '') {
  window.dispatchEvent(new CustomEvent('meeting-recording-direct-status', { detail: {
    recordingId: session.id, roomId: session.roomId, state, message,
    bytes: session.totalBytes, uploadedBytes: session.uploadedBytes,
    bufferedBytes: Math.max(0, session.totalBytes - session.uploadedBytes),
    captureEnded: session.captureEnded === true,
    percentage: session.totalBytes ? Math.floor(session.uploadedBytes / session.totalBytes * 100) : 0,
  } }));
}
function invalidDestination() {
  return Object.assign(new Error('The server returned an invalid direct recording destination.'), { permanent: true });
}
function validateUploadUrl(value, allowedUploadHosts) {
  let url;
  try { url = new URL(value); } catch { throw invalidDestination(); }
  if (!Array.isArray(allowedUploadHosts) || !allowedUploadHosts.length ||
      !allowedUploadHosts.every(host => typeof host === 'string' && /^[a-z0-9.-]+$/.test(host)) ||
      url.protocol !== 'https:' || !/(^|\.)idrivee2(?:-\d+)?\.com$/i.test(url.hostname) || !allowedUploadHosts.includes(url.hostname) ||
      url.username || url.password || url.port || url.hash) throw invalidDestination();
  return url.href;
}
function validateUploadHeaders(headers) {
  if (!headers || typeof headers !== 'object' || Array.isArray(headers)) throw invalidDestination();
  const normalized = {};
  for (const [name, value] of Object.entries(headers)) {
    const key = name.toLowerCase();
    if (key !== 'content-type' || normalized[key] ||
        typeof value !== 'string' || !value || /[\r\n]/.test(value)) throw invalidDestination();
    normalized[key] = value;
  }
  if (normalized['content-type'] !== 'application/octet-stream') throw invalidDestination();
  return { 'Content-Type': normalized['content-type'] };
}
function assertE2Session(session) {
  if ((session.transport || LEGACY_TRANSPORT) !== TRANSPORT || session.storageProvider !== 'idrive-e2') {
    throw new Error('This saved recording uses the retired recording transport and is read-only. Contact the administrator to recover it.');
  }
  if (![NEW_PART_BYTES, PART_BYTES].includes(session.partBytes)) throw new Error('The recording segment size is not supported. Refresh the app and try again.');
}
async function digestBlob(blob) {
  if (!globalThis.crypto?.subtle) throw new Error('Secure recording uploads need browser SHA-256 support. Use a current browser over HTTPS.');
  const digest = await globalThis.crypto.subtle.digest('SHA-256', await blob.arrayBuffer());
  return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
}
function retryDelay(error, attempt) {
  return Math.max(retryAfterMs(error), Math.min(15000, 1000 * 2 ** attempt));
}
function retryAfterMs(error) {
  const headers = error?.response?.headers;
  const raw = headers?.get?.('retry-after') ?? headers?.['retry-after'] ?? error?.retryAfter;
  if (raw == null || raw === '') return 0;
  const seconds = Number(raw);
  const ms = Number.isFinite(seconds) ? seconds * 1000 : Date.parse(raw) - Date.now();
  return Number.isFinite(ms) ? Math.max(0, Math.min(300000, ms)) : 0;
}
function pauseInsteadOfRetry(error) {
  return retryAfterMs(error) > 15000 || [502, 504].includes(error?.response?.status) || !error?.response;
}
function isMissingUpload(error) {
  return ['SEGMENT_UPLOAD_MISSING', 'SEGMENT_BYTES_MISMATCH'].includes(error.response?.data?.code);
}
function isPermanentError(error) {
  if (error.response?.data?.code === 'E2_RECORDING_BUSY') return false;
  return error.permanent || [401, 400, 403, 404, 409, 410, 413, 422].includes(error.response?.status || error.status);
}
function verifiedCompletion(session, data) {
  if (!Number.isSafeInteger(session.totalBytes) || session.totalBytes < 1 ||
      session.uploadedBytes !== session.totalBytes || !session.verifiedParts?.length ||
      session.verifiedParts.reduce((total, part) => total + part.bytes, 0) !== session.totalBytes ||
      data?.completed !== true || data.bytes !== session.totalBytes ||
      !Array.isArray(data.parts) || data.parts.length !== session.verifiedParts.length ||
      session.verifiedParts.length !== session.nextPart - 1 ||
      !session.verifiedParts.every(part => data.parts.some(remote =>
        remote && remote.number === part.number && remote.bytes === part.bytes && remote.sha256 === part.sha256))) {
    throw new Error('The server has not confirmed every captured recording byte. Your saved recording is retained in this browser. Resume upload to check it again.');
  }
}

export class DirectRecordingUpload {
  constructor(session) { this.session = session; this.queue = Promise.resolve(); this.flushPromise = null; this.stopped = false; this.failure = null; this.persistenceFailure = null; this.useRelay = Boolean(session.preferRelay || session.pendingPart?.useRelay); }
  static async create({ recordingId, roomId, title, mimeType, maxBytes, partBytes, storageProvider, transport = LEGACY_TRANSPORT }) {
    // Successful round-trip before recording starts confirms writable storage.
    const session = { id: String(recordingId), roomId, owner: String(getUserId()), title, mimeType,
      maxBytes, partBytes, storageProvider, transport, verifiedParts: [], totalBytes: 0, uploadedBytes: 0, nextPart: 1, sequence: 0,
      captureEnded: false, updatedAt: Date.now(), createdAt: Date.now() };
    assertE2Session(session);
    if (!Number.isSafeInteger(maxBytes) || maxBytes < partBytes) throw new Error('The recording size limit is invalid.');
    if (!globalThis.crypto?.subtle) throw new Error('Secure recording uploads need browser SHA-256 support. Use a current browser over HTTPS.');
    const release = await acquireCaptureLock(session.id);
    try { await write(sessions => sessions.put(session)); } catch (error) { release(); throw error; }
    navigator.storage?.persist?.().catch(() => {});
    const upload = new DirectRecordingUpload(session); upload.releaseLock = release; active.set(session.id, upload);
    upload.heartbeat = window.setInterval(() => { void api.post(upload.path('heartbeat'), {}).catch(() => {}); }, 30000);
    upload.startRetry();
    emit(session, 'recording'); return upload;
  }
  path(action) { return `/recordings/${encodeURIComponent(this.session.roomId)}/${encodeURIComponent(this.session.id)}/direct/${action}`; }
  startRetry() {
    this.retryTimer = window.setInterval(() => { void this.retryUpload(); }, 15000);
    this.onlineHandler = () => { void this.retryUpload(); };
    window.addEventListener('online', this.onlineHandler);
  }
  stopRetry() {
    window.clearInterval(this.retryTimer);
    if (this.onlineHandler) window.removeEventListener('online', this.onlineHandler);
  }
  async retryUpload() {
    if (this.stopped || this.finishing || !this.failure || this.persistenceFailure || this.flushPromise || Date.now() < (this.retryNotBefore || 0)) return;
    this.failure = null;
    try { await this.flush(false); }
    catch (error) { this.reportFailure(error); }
  }
  async add(blob) {
    assertE2Session(this.session);
    this.queue = this.queue.then(async () => {
      if (this.persistenceFailure) throw this.persistenceFailure;
      const session = this.session;
      if (session.totalBytes + blob.size > session.maxBytes || session.totalBytes - session.uploadedBytes + blob.size > MAX_QUEUED_BYTES) {
        throw new Error('The recording upload is too far behind or reached its size limit. Capture stopped; resume the saved recording after your connection recovers.');
      }
      const next = { ...session, totalBytes: session.totalBytes + blob.size, sequence: session.sequence + 1, updatedAt: Date.now() };
      await write((sessions, chunks) => {
        sessions.put(next); chunks.put({ recordingId: next.id, sequence: next.sequence, blob });
      });
      this.session = next;
      emit(next, this.failure ? 'paused' : next.uploadedBytes ? 'recording' : 'buffered');
      // An upload pause must not prevent MediaRecorder's final dataavailable
      // event from being saved. Only persistence failures block local capture.
      if (!this.failure) void this.flush(false).catch(error => this.reportFailure(error));
    }).catch(error => { this.persistenceFailure = error; throw error; });
    return this.queue;
  }
  reportFailure(error) {
    if (this.failure) return;
    this.failure = error;
    this.deferRetry(error);
    // A network/upload pause is not a capture failure. Keep the same encoder
    // and recording identity alive while its bounded durable queue is retried.
    const fatal = Boolean(this.persistenceFailure || error.captureFatal || error.response?.status === 403);
    if (fatal) this.onFailure?.(error);
    emit(this.session, 'paused', error.message);
    if (fatal) window.dispatchEvent(new CustomEvent('meeting-recording-capture-error', { detail: {
      roomId: this.session.roomId, recordingId: this.session.id,
      message: `${error.message} Saved parts remain in this browser. Open More → Recording uploads to recover.`,
    } }));
  }
  deferRetry(error) {
    this.lastUploadError = error;
    this.retryNotBefore = Date.now() + Math.max(60000, retryAfterMs(error));
  }
  async savePendingPart(pendingPart) {
    this.queue = this.queue.then(async () => {
      const next = { ...this.session, pendingPart, preferRelay: this.useRelay === true, updatedAt: Date.now() };
      await write(sessions => sessions.put(next));
      this.session = next;
    });
    await this.queue;
  }
  async registerPart(partNumber, intentId) {
    let lastError;
    for (let attempt = 0; attempt < 3; attempt += 1) {
      try {
        const { data } = await api.post(this.path('register'), { partNumber, intentId }, { timeout: 180_000 });
        if (data?.verified !== true) throw Object.assign(new Error('The server did not verify the uploaded recording segment.'), { permanent: true });
        return;
      } catch (error) {
        lastError = error;
        if (isPermanentError(error) || pauseInsteadOfRetry(error) || attempt === 2) { this.deferRetry(error); throw error; }
        await sleep(retryDelay(error, attempt));
      }
    }
    throw lastError;
  }
  async requestWithRetry(method, action, body) {
    for (let attempt = 0; attempt < 3; attempt += 1) {
      try {
        // The shared API's 20-second default is too short for server-side media
        // verification. Keep metadata reads bounded but let completion finish.
        const options = { timeout: action.endsWith('/complete') ? 600_000 : 20_000 };
        return await (method === 'get' ? api.get(action, options) : api.post(action, body, options));
      }
      catch (error) {
        if (isPermanentError(error) || pauseInsteadOfRetry(error) || attempt === 2) { this.deferRetry(error); throw error; }
        await sleep(retryDelay(error, attempt));
      }
    }
  }
  async relayPart(blob, partNumber, pending) {
    // The existing owner-bound intent, size and SHA-256 remain authoritative.
    this.useRelay = true;
    await this.savePendingPart({ ...pending, useRelay: true });
    const { data } = await api.post(this.path(`relay/${partNumber}/${encodeURIComponent(pending.intentId)}`), blob,
      { headers: { 'Content-Type': 'application/octet-stream' }, timeout: 180000 });
    if (data?.verified !== true) throw Object.assign(new Error('The recording relay did not verify this segment.'), { permanent: true });
  }
  async putPart(blob, partNumber, final) {
    assertE2Session(this.session);
    const sha256 = await digestBlob(blob);
    let pending = this.session.pendingPart;
    if (pending && (pending.partNumber !== partNumber || pending.bytes !== blob.size || pending.sha256 !== sha256)) {
      throw new Error('The saved recording segment no longer matches its upload intent. Contact the administrator.');
    }
    // Bind the final flag before even requesting a URL: the response can be
    // lost after the server saves its segment, and recovery must reuse that flag.
    if (!pending) {
      pending = { partNumber, bytes: blob.size, sha256, final, intentId: null };
      await this.savePendingPart(pending);
    }
    // Preserve the exact intent before sending media. If a tab closes or a
    // response is lost, registration can verify that PUT without sending it again.
    if (pending?.intentId) {
      try { await this.registerPart(partNumber, pending.intentId); return sha256; }
      catch (error) {
        if (error.response?.data?.code === 'SEGMENT_UPLOAD_UNCONFIRMED' ||
            (isMissingUpload(error) && (pending.useRelay || pending.putRejected))) {
          await this.relayPart(blob, partNumber, pending); return sha256;
        }
        if (!isMissingUpload(error)) throw error;
        pending = { ...pending, intentId: null };
        await this.savePendingPart(pending);
      }
    }
    let rejectedPuts = 0;
    for (let attempt = 0; attempt < 6; attempt += 1) {
      let putError;
      try {
        const { data } = await api.post(this.path('part'), { partNumber, bytes: blob.size, final: pending.final, sha256 });
        if (data.verified === true) return sha256;
        if (data.partNumber !== partNumber || typeof data.intentId !== 'string' || !data.intentId) throw invalidDestination();
        const destination = validateUploadUrl(data.url, data.allowedUploadHosts);
        const headers = validateUploadHeaders(data.headers);
        pending = { partNumber, bytes: blob.size, sha256, final: pending.final, intentId: data.intentId };
        await this.savePendingPart(pending);
        // The authenticated relay uses the same persisted intent and digest.
        // It works even when browser-to-e2 PUT is blocked by bucket CORS.
        if (this.useRelay) { await this.relayPart(blob, partNumber, pending); return sha256; }
        const controller = new AbortController();
        const timer = window.setTimeout(() => controller.abort(), 180000);
        try {
          const response = await fetch(destination, { method: 'PUT', headers, body: blob, credentials: 'omit', redirect: 'error', signal: controller.signal });
          if (!response.ok) {
            const error = new Error(`Direct recording upload failed (${response.status}).`);
            error.status = response.status;
            error.retryAfter = Math.max(0, Math.min(30, Number(response.headers.get('Retry-After')) || 0));
            putError = error;
          }
        } catch (error) { putError = error; }
        finally { window.clearTimeout(timer); }
        if ([400, 401, 403].includes(putError?.status)) {
          rejectedPuts += 1;
          pending = { ...pending, putRejected: true };
          await this.savePendingPart(pending);
        }
      } catch (error) {
        if (isPermanentError(error) || pauseInsteadOfRetry(error) || attempt === 5) { this.deferRetry(error); throw error; }
        await sleep(retryDelay(error, attempt));
        continue;
      }
      // Register even after an uncertain PUT result. Retrying registration must
      // never create another upload while the provider may already have the bytes.
      try { await this.registerPart(partNumber, pending.intentId); return sha256; }
      catch (error) {
        if (error.response?.data?.code === 'SEGMENT_UPLOAD_UNCONFIRMED') {
          await this.relayPart(blob, partNumber, pending); return sha256;
        }
        if (!isMissingUpload(error)) throw error;
        if (putError && (!putError.status || putError.status >= 500 || rejectedPuts >= 2)) {
          await this.relayPart(blob, partNumber, pending); return sha256;
        }
        if (attempt === 5) throw error;
        pending = { ...pending, intentId: null };
        await this.savePendingPart(pending);
        await sleep(retryDelay(putError || error, attempt));
      }
    }
    throw new Error('The recording segment could not be uploaded. Resume upload to retry.');
  }
  async flush(final) {
    if (this.failure) return;
    if (this.flushPromise) { await this.flushPromise; if (!final) return; }
    this.flushPromise = (async () => {
      while (true) {
        if (this.stopped || this.failure) break;
        // Serialize reading/saving with capture writes, then release during PUT.
        await this.queue;
        const chunks = await chunksFor(this.session.id);
        const available = chunks.reduce((n, chunk) => n + chunk.blob.size, 0);
        if (!available || (!final && available < this.session.partBytes)) break;
        const length = Math.min(this.session.partBytes, available);
        const blob = new Blob(chunks.map(c => c.blob), { type: this.session.mimeType }).slice(0, length);
        const sha256 = await this.putPart(blob, this.session.nextPart, final && available <= this.session.partBytes);
        // Capture may have appended chunks while uploading. Mutate only consumed chunks.
        this.queue = this.queue.then(async () => {
          let remaining = length;
          const next = { ...this.session, uploadedBytes: this.session.uploadedBytes + length, nextPart: this.session.nextPart + 1, updatedAt: Date.now(), lastPartBytes: length, pendingPart: null,
            verifiedParts: [...this.session.verifiedParts, { number: this.session.nextPart, bytes: length, sha256 }] };
          await write((sessions, store) => {
            for (const chunk of chunks) {
              if (!remaining) break;
              if (chunk.blob.size <= remaining) { store.delete([chunk.recordingId, chunk.sequence]); remaining -= chunk.blob.size; }
              else { store.put({ ...chunk, blob: chunk.blob.slice(remaining) }); remaining = 0; }
            }
            sessions.put(next);
          });
          this.session = next;
          this.lastUploadError = null;
          this.retryNotBefore = 0;
          emit(this.session, this.session.captureEnded ? 'uploading' : 'recording');
        });
        await this.queue;
      }
    })();
    try { await this.flushPromise; } finally { this.flushPromise = null; }
  }
  async finish({ interrupted = false } = {}) {
    assertE2Session(this.session);
    window.clearInterval(this.heartbeat); this.stopRetry(); this.finishing = true;
    // A failed upload is recoverable without changing the recording's identity.
    await this.queue.catch(() => {});
    // A quota/storage failure means the final MediaRecorder bytes were not
    // committed locally. Never label that incomplete capture as safely saved.
    if (this.persistenceFailure) throw this.persistenceFailure;
    this.queue = Promise.resolve(); this.failure = null;
    this.session.captureEnded = true; this.session.interrupted = interrupted; this.session.updatedAt = Date.now();
    await write(sessions => sessions.put(this.session));
    emit(this.session, 'uploading');
    await this.requestWithRetry('post', `/recordings/${encodeURIComponent(this.session.roomId)}/${encodeURIComponent(this.session.id)}/stop`, {}).catch(error => {
      // Completing the verified manifest also closes capture atomically. A
      // temporary /stop failure must not strand the captured tail locally.
      if (error.response?.status !== 404 && (isPermanentError(error) || pauseInsteadOfRetry(error))) throw error;
    });
    await this.flush(true);
    if (!this.session.totalBytes || this.session.uploadedBytes !== this.session.totalBytes) {
      throw new Error('Recording capture did not produce a complete set of media bytes. Saved media remains in this browser.');
    }
    let completionError;
    let data;
    try {
      ({ data } = await this.requestWithRetry('post', this.path('complete'), {
        partCount: this.session.nextPart - 1, bytes: this.session.totalBytes,
        interrupted: this.session.interrupted === true,
      }));
    } catch (error) { completionError = error; }
    // The response can be lost after commit. Confirm the immutable manifest
    // before deleting the durable session, even when /complete returned 200.
    try {
      let complete = false;
      for (let poll = 0; poll < 360; poll += 1) {
        const status = await this.requestWithRetry('get', this.path('status'));
        if (status.data?.completed) { verifiedCompletion(this.session, status.data); complete = true; break; }
        if (status.data?.error && !status.data?.finalizing) throw new Error(status.data.error);
        if (completionError && !status.data?.finalizing && completionError.response?.data?.code !== 'E2_RECORDING_BUSY') throw completionError;
        if (poll > 0 && poll % 6 === 0 && !status.data?.finalizing) {
          await this.requestWithRetry('post', this.path('complete'), { partCount: this.session.nextPart - 1, bytes: this.session.totalBytes, interrupted: this.session.interrupted === true });
        }
        emit(this.session, 'verifying', 'Upload complete. Verifying the saved recording.');
        await sleep(5000);
      }
      if (!complete) throw new Error('Recording verification is still pending. Open Recording uploads to check it again.');
    } catch (error) { throw error; }
    await write(sessions => sessions.delete(this.session.id));
    active.delete(this.session.id); this.releaseLock?.(); emit(this.session, 'completed');
    return { ...data, status: 'completed', verified: true, recordingId: this.session.id,
      bytes: this.session.totalBytes, partCount: this.session.nextPart - 1 };
  }
  async discard() {
    this.stopped = true;
    window.clearInterval(this.heartbeat); this.stopRetry();
    try {
      await this.flushPromise?.catch(() => {});
      await this.queue.catch(() => {});
      const chunks = await chunksFor(this.session.id);
      await write((sessions, store) => { sessions.delete(this.session.id); chunks.forEach(chunk => store.delete([chunk.recordingId, chunk.sequence])); });
    } finally { active.delete(this.session.id); this.releaseLock?.(); }
  }
  async preserve(error = this.lastUploadError) {
    this.stopped = true;
    window.clearInterval(this.heartbeat); this.stopRetry();
    try {
      await this.flushPromise?.catch(() => {});
      await this.queue.catch(() => {});
      this.session.interrupted = this.session.interrupted === true || !this.session.captureEnded;
      this.session.captureEnded = true;
      this.session.captureIncomplete = this.session.captureIncomplete === true || Boolean(this.persistenceFailure);
      this.session.retryAt = Date.now() + Math.max(60000, retryAfterMs(error));
      this.session.recoveryBlocked = this.session.captureIncomplete || Boolean(error && isPermanentError(error));
      await write(sessions => sessions.put(this.session));
    } finally { active.delete(this.session.id); this.releaseLock?.(); }
    emit(this.session, 'paused', this.session.recoveryBlocked
      ? 'Saved media needs attention. Open Recording uploads to review and retry.'
      : 'Saved locally. Upload will retry automatically. Keep this tab open.');
  }
}

export async function pendingDirectRecordings(roomId = '', { includeActive = false } = {}) {
  const sessions = await read('sessions', null, true);
  return sessions.filter(s => s.owner === String(getUserId()) && (!roomId || s.roomId === roomId) && (includeActive || !active.has(s.id)))
    .map(session => ({ ...session, transport: session.transport || LEGACY_TRANSPORT, recoverable: session.transport === TRANSPORT && session.storageProvider === 'idrive-e2' }));
}
export async function resumeDirectRecording(id) {
  const release = await acquireCaptureLock(id);
  let upload;
  try {
  let session = await read('sessions', id);
  if (!session || session.owner !== String(getUserId())) throw new Error('This saved recording belongs to a different account or is no longer available.');
  if (active.has(id)) throw new Error('This recording is already active in this tab.');
  const needsMigration = session.transport !== TRANSPORT || session.storageProvider !== 'idrive-e2';
  if (needsMigration && (session.transport !== MIGRATABLE_TRANSPORT || session.storageProvider !== 'spacebyte')) assertE2Session(session);
  upload = new DirectRecordingUpload(session); upload.releaseLock = release;
  const { data } = await api.get(upload.path('status'));
  if (needsMigration && (data.provider !== 'idrive-e2' || data.transport !== TRANSPORT || data.migratedFrom !== 'spacebyte')) throw new Error('The administrator must first verify and migrate this saved recording. Your local media remains unchanged.');
  // A committed local segment must still have the same size and SHA-256
  // remotely. Never skip missing or changed bytes during recovery.
  if (!Array.isArray(data.parts) || !Array.isArray(session.verifiedParts) || session.verifiedParts.length !== session.nextPart - 1) {
    throw new Error('The saved recording verification state is incomplete. Contact the administrator.');
  }
  for (const part of session.verifiedParts) {
    if (!data.parts.some(remote => remote.number === part.number && remote.bytes === part.bytes && remote.sha256 === part.sha256)) {
      throw new Error('An earlier uploaded recording segment is missing or changed. Contact the administrator.');
    }
  }
  if (data.completed) {
    if (data.bytes !== session.totalBytes) throw new Error('The completed recording size differs from the saved browser recording. Local media is retained for review.');
    verifiedCompletion(session, data);
    const chunks = await chunksFor(id);
    await write((sessions, store) => { sessions.delete(id); chunks.forEach(chunk => store.delete([chunk.recordingId, chunk.sequence])); });
    emit(session, 'completed');
    return { status: 'completed', verified: true, recordingId: id, bytes: session.totalBytes, partCount: session.nextPart - 1 };
  }
  if (needsMigration) {
    // Only a server-verified migration and matching acknowledged hashes authorize
    // this one-way metadata upgrade. Discard only the old presign intent, not bytes.
    session = { ...session, storageProvider: 'idrive-e2', transport: TRANSPORT, pendingPart: null };
    await write(sessions => sessions.put(session));
    upload.session = session;
  }
  assertE2Session(session);
  active.set(id, upload);
  return await upload.finish({ interrupted: !session.captureEnded || session.interrupted });
  } catch (error) {
    if (upload) await upload.preserve(error).catch(() => {});
    throw error;
  } finally { active.delete(id); release(); }
}

let recoveryRun = null;
export async function recoverStoppedRecordings() {
  if (recoveryRun) return recoveryRun;
  recoveryRun = (async () => {
    if (navigator.onLine === false || !getUserId()) return;
    const rows = await pendingDirectRecordings();
    for (const row of rows) {
      // Automatic recovery only resumes a deliberately stopped, durable capture.
      // An open recorder in another tab remains protected by its Web Lock.
      if (!row.recoverable || !row.captureEnded || !row.totalBytes || row.captureIncomplete || row.recoveryBlocked || Date.now() < (row.retryAt || 0)) continue;
      try { await resumeDirectRecording(row.id); }
      catch { /* The exact bytes and backoff remain in IndexedDB. */ }
    }
  })();
  try { return await recoveryRun; } finally { recoveryRun = null; }
}

// Mounted once by the authenticated app shell. Route changes and closing the
// optional status panel do not stop durable uploads after Stop/Class End.
export function startRecordingRecovery() {
  let stopped = false;
  const run = () => { if (!stopped) void recoverStoppedRecordings().catch(() => {}); };
  const changed = event => { if (event.detail?.state === 'paused' && event.detail?.captureEnded) run(); };
  const timer = window.setInterval(run, 15000);
  window.addEventListener('online', run);
  window.addEventListener('meeting-recording-direct-status', changed);
  run();
  return () => { stopped = true; window.clearInterval(timer); window.removeEventListener('online', run); window.removeEventListener('meeting-recording-direct-status', changed); };
}
export { validateUploadUrl, validateUploadHeaders };
